import { InlineNotice } from '@/components/feedback/inline-notice'
import { FormField } from '@/components/forms/form-field'
import { PasswordInput } from '@/components/forms/password-input'
import { Button } from '@/components/primitives/button'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { MobileAuthLayout } from '@/features/auth/mobile/mobile-auth-layout'
import { usePasswordResetPage } from '@/features/auth/password-reset/use-password-reset-page'
import { resolveBrandAssetSrc } from '@/features/shared/brand/branding-core'
import { useBranding } from '@/features/shared/brand/branding-hooks'
import { setAppLanguage } from '@/i18n/i18n'

export function MobilePasswordResetPage() {
  const {
    cancel,
    confirmPassword,
    currentPassword,
    errors,
    language,
    newPassword,
    resetMutation,
    setConfirmPassword,
    setCurrentPassword,
    setErrors,
    setNewPassword,
    t,
    validate,
  } = usePasswordResetPage('user')
  const {
    settings: { logo },
  } = useBranding()

  return (
    <MobileAuthLayout
      actions={
        <LanguageSegmentedControl
          language={language}
          onLanguageChange={(nextLanguage) => void setAppLanguage(nextLanguage)}
          t={t}
        />
      }
    >
      <div>
        <header className="flex flex-col items-center text-center">
          <img alt="" className="size-11 object-contain" src={resolveBrandAssetSrc(logo)} />
          <h2 className="mt-4 text-[21px] font-semibold leading-[1.2] text-[var(--color-text)]">
            {t('passwordReset.title')}
          </h2>
          <p className="mt-2 text-[13px] leading-5 text-[var(--color-text-muted)]">
            {t('passwordReset.subtitle')}
          </p>
        </header>

        {errors.root ? (
          <InlineNotice className="mt-5 text-[13px] leading-[19px]" role="alert" tone="danger">
            {errors.root}
          </InlineNotice>
        ) : null}

        <form
          className="mt-[26px] grid gap-4 [&_label]:text-[13px] [&_label]:font-normal [&_label]:leading-none [&_label]:text-[#374151] [&_label_span]:hidden [&_p]:text-xs [&_p]:leading-[17px]"
          onSubmit={(event) => {
            event.preventDefault()
            if (!validate()) return
            resetMutation.mutate()
          }}
        >
          <FormField
            error={errors.currentPassword}
            htmlFor="user-reset-current-password"
            label={t('passwordReset.currentPassword')}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              className="h-[46px] rounded-lg border-[var(--color-control-border)] text-[15px]"
              hideLabel={t('common.hidePassword')}
              id="user-reset-current-password"
              invalid={Boolean(errors.currentPassword)}
              onChange={(event) => {
                setCurrentPassword(event.target.value)
                setErrors((current) => ({
                  ...current,
                  currentPassword: undefined,
                  root: undefined,
                }))
              }}
              placeholder={t('passwordReset.currentPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={currentPassword}
            />
          </FormField>

          <FormField
            error={errors.newPassword}
            htmlFor="user-reset-new-password"
            label={t('passwordReset.newPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              className="h-[46px] rounded-lg border-[var(--color-control-border)] text-[15px]"
              hideLabel={t('common.hidePassword')}
              id="user-reset-new-password"
              invalid={Boolean(errors.newPassword)}
              onChange={(event) => {
                setNewPassword(event.target.value)
                setErrors((current) => ({ ...current, newPassword: undefined, root: undefined }))
              }}
              placeholder={t('passwordReset.newPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={newPassword}
            />
          </FormField>

          <FormField
            error={errors.confirmPassword}
            htmlFor="user-reset-confirm-password"
            label={t('passwordReset.confirmPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              className="h-[46px] rounded-lg border-[var(--color-control-border)] text-[15px]"
              hideLabel={t('common.hidePassword')}
              id="user-reset-confirm-password"
              invalid={Boolean(errors.confirmPassword)}
              onChange={(event) => {
                setConfirmPassword(event.target.value)
                setErrors((current) => ({
                  ...current,
                  confirmPassword: undefined,
                  root: undefined,
                }))
              }}
              placeholder={t('passwordReset.confirmPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={confirmPassword}
            />
          </FormField>

          <div className="grid">
            <Button
              className="h-[46px] rounded-lg text-base font-bold"
              isLoading={resetMutation.isPending}
              type="submit"
            >
              {t('passwordReset.submit')}
            </Button>
            <Button
              className="mt-[18px] h-11 text-[13px] text-[var(--color-primary)]"
              disabled={resetMutation.isPending}
              onClick={cancel}
              type="button"
              variant="ghost"
            >
              {t('passwordReset.backToLogin')}
            </Button>
          </div>
        </form>
      </div>
    </MobileAuthLayout>
  )
}
